import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  Chip,
  Button,
  CircularProgress,
  Alert,
  Divider,
} from '@mui/material';
import {
  ArrowBack as BackIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Visibility as ViewIcon,
  PushPin as PinIcon,
} from '@mui/icons-material';
import { newsAPI, News } from '../api/news.api';
import { reportViewedObject } from '../hooks/usePageViewLogger';
import { useNavigate, useParams } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { RootState } from '../store';

const NewsDetailPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { user } = useSelector((state: RootState) => state.auth);
  const [news, setNews] = useState<News | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const isAdmin = user?.role === 'ADMIN' || user?.role === 'HEAD_COACH';

  useEffect(() => {
    if (id) {
      fetchNews();
    }
  }, [id]);

  const fetchNews = async () => {
    try {
      setLoading(true);
      setError('');
      const article = await newsAPI.getNewsById(id!);
      setNews(article);
      const title = (article as any)?.title || (article as any)?.data?.title;
      if (title) {
        reportViewedObject(`/news/${id}`, title);
      }
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to load news article');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async () => {
    if (!window.confirm('Are you sure you want to delete this news article?')) {
      return;
    }

    try {
      await newsAPI.deleteNews(id!);
      navigate('/news');
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to delete news article');
    }
  };

  const getCategoryColor = (cat: string) => {
    switch (cat) {
      case 'announcement':
        return 'primary';
      case 'event':
        return 'secondary';
      case 'achievement':
        return 'success';
      default:
        return 'default';
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error || !news) {
    return (
      <Box>
        <Alert severity="error">{error || 'News article not found'}</Alert>
        <Button
          startIcon={<BackIcon />}
          onClick={() => navigate('/news')}
          sx={{ mt: 2 }}
        >
          Back to News
        </Button>
      </Box>
    );
  }

  return (
    <Box>
      <Box sx={{ mb: 3, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Button startIcon={<BackIcon />} onClick={() => navigate('/news')}>
          Back to News
        </Button>
        {isAdmin && (
          <Box sx={{ display: 'flex', gap: 1 }}>
            <Button
              variant="outlined"
              startIcon={<EditIcon />}
              onClick={() => navigate(`/news/${id}/edit`)}
            >
              Edit
            </Button>
            <Button
              variant="outlined"
              color="error"
              startIcon={<DeleteIcon />}
              onClick={handleDelete}
            >
              Delete
            </Button>
          </Box>
        )}
      </Box>

      <Paper sx={{ p: 4 }}>
        {news.imageUrl && (
          <Box
            component="img"
            src={news.imageUrl}
            alt={news.title}
            sx={{
              width: '100%',
              maxHeight: 400,
              objectFit: 'cover',
              borderRadius: 1,
              mb: 3,
            }}
          />
        )}

        <Box sx={{ display: 'flex', gap: 1, mb: 2, flexWrap: 'wrap' }}>
          {news.isPinned && (
            <Chip icon={<PinIcon />} label="Pinned" size="small" color="warning" />
          )}
          {news.category && (
            <Chip
              label={news.category}
              size="small"
              color={getCategoryColor(news.category)}
            />
          )}
          {!news.isPublished && isAdmin && (
            <Chip label="Draft" size="small" color="default" />
          )}
        </Box>

        <Typography variant="h3" component="h1" gutterBottom>
          {news.title}
        </Typography>

        <Box sx={{ display: 'flex', gap: 3, mb: 3 }}>
          <Typography variant="body2" color="text.secondary">
            By {news.creator?.firstName} {news.creator?.lastName}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {new Date(news.publishedAt || news.createdAt).toLocaleDateString()}
          </Typography>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <ViewIcon fontSize="small" color="action" />
            <Typography variant="body2" color="text.secondary">
              {news.viewCount} views
            </Typography>
          </Box>
        </Box>

        <Divider sx={{ mb: 3 }} />

        {news.excerpt && (
          <Typography
            variant="subtitle1"
            color="text.secondary"
            paragraph
            sx={{ fontStyle: 'italic' }}
          >
            {news.excerpt}
          </Typography>
        )}

        <Typography
          variant="body1"
          component="div"
          dangerouslySetInnerHTML={{ __html: news.content }}
          sx={{
            '& p': { mb: 2 },
            '& img': { maxWidth: '100%', height: 'auto' },
            '& h1, & h2, & h3, & h4, & h5, & h6': { mt: 3, mb: 2 },
            '& ul, & ol': { pl: 4, mb: 2 },
          }}
        />

        <Divider sx={{ my: 3 }} />

        <Typography variant="caption" color="text.secondary">
          Last updated: {new Date(news.updatedAt).toLocaleString()}
        </Typography>
      </Paper>
    </Box>
  );
};

export default NewsDetailPage;
